import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { botService } from '../services/BotService';
import { userService } from '../services/UserService';
import { useAuthStore } from '../../features/auth/store/authStore';
import { useChatStore } from '../../features/chat/store/chatStore';
import { shallow } from 'zustand/shallow';
import type { Model } from '../types/api.types';
import i18n from '../../i18n';

/**
 * Parse Gemini version from model name
 * Examples: "Gemini 2.0 Flash" -> 2.0, "Gemini 1.5 Pro" -> 1.5
 */
const parseGeminiVersion = (modelName: string): number => {
  const match = modelName.match(/(\d+\.\d+)/);
  return match ? parseFloat(match[1]) : 0;
};

export function useModels(options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { currentModel, setCurrentModel } = useChatStore(
    state => ({
      currentModel: state.currentModel,
      setCurrentModel: state.setCurrentModel,
    }),
    shallow
  );

  // Default enabled to true for backward compatibility
  const enabled = options?.enabled !== false;

  // Fetch available models
  const {
    data: models = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['models'],
    queryFn: () => botService.getAllModels(),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Update user's default model
  const updateDefaultModel = useMutation({
    mutationFn: async (model: Model) => {
      if (!user) throw new Error(i18n.t('errors.general.notAuthenticated'));
      return userService.updateUserModel(model);
    },
    onSuccess: updatedUser => {
      // Update cache directly with the response data instead of refetching
      queryClient.setQueryData(['user', user?.uid], updatedUser);
    },
  });

  // Select a model for current session
  const selectModel = async (model: Model) => {
    setCurrentModel(model);

    // Optionally update user's default
    if (user) {
      await updateDefaultModel.mutateAsync(model);
    }
  };

  // Sort models: Gemini models first (newest to oldest), then others in default order
  const sortedModels = [...models].sort((a, b) => {
    const aIsGemini = a.name.startsWith('Gemini');
    const bIsGemini = b.name.startsWith('Gemini');

    // Both are Gemini: sort by version descending (newest first)
    if (aIsGemini && bIsGemini) {
      const aVersion = parseGeminiVersion(a.name);
      const bVersion = parseGeminiVersion(b.name);
      return bVersion - aVersion;
    }

    // Only a is Gemini: a comes first
    if (aIsGemini) return -1;

    // Only b is Gemini: b comes first
    if (bIsGemini) return 1;

    // Neither is Gemini: keep original order
    return 0;
  });

  return {
    models: sortedModels,
    currentModel,
    isLoading,
    error,
    selectModel,
    updateDefaultModel: updateDefaultModel.mutate,
    isUpdating: updateDefaultModel.isPending,
    isSwitchingModel: updateDefaultModel.isPending,
    refetch,
  };
}
